import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'

const STORAGE_KEY = 'cjm_cookie_consent'

export default function CookieBanner() {
  const [visible, setVisible] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      const t = setTimeout(() => setVisible(true), 1200)
      return () => clearTimeout(t)
    }
  }, [])

  const choose = (accepted) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ accepted, date: new Date().toISOString() }))
    setVisible(false)
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-banner"
          role="dialog"
          aria-label="Cookie consent"
          initial={reduceMotion ? { opacity: 0 } : { y: 40, opacity: 0 }}
          animate={reduceMotion ? { opacity: 1 } : { y: 0, opacity: 1 }}
          exit={reduceMotion ? { opacity: 0 } : { y: 40, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="cjm-cookie"
        >
          <div className="cjm-cookie-inner">
            {/* Ambient glow orb */}
            <div aria-hidden="true" style={{
              position: 'absolute', top: -50, right: -50, width: 160, height: 160,
              background: 'radial-gradient(circle, rgba(6,182,212,0.16), transparent 70%)',
              pointerEvents: 'none',
            }} />

            {/* Top glow line */}
            <div aria-hidden="true" style={{
              position: 'absolute', top: 0, left: '10%', right: '10%', height: 1,
              background: 'linear-gradient(90deg,transparent,rgba(6,182,212,0.4),transparent)',
              borderRadius: 1,
            }} />

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              {/* Cookie icon */}
              <div aria-hidden="true" style={{
                width: 34, height: 34, flexShrink: 0, borderRadius: 10,
                background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22d3ee',
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
                  <path d="M8.5 8.5v.01M16 15.5v.01M12 12v.01"/>
                </svg>
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: '#fff', fontSize: 13.5, fontWeight: 600, marginBottom: 4, fontFamily: 'Geist Variable, sans-serif' }}>
                  We use cookies
                </p>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12.5, lineHeight: 1.65 }}>
                  Local storage only — to remember your preferences. No tracking or ads.{' '}
                  <button
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    className="cjm-cookie-link"
                    style={{ background: 'none', border: 'none', color: '#22d3ee', fontSize: 12.5, cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3, touchAction: 'manipulation' }}
                  >
                    {expanded ? 'Hide details' : 'Learn more'}
                  </button>
                </p>
              </div>
            </div>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: reduceMotion ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                  style={{ overflow: 'hidden' }}
                >
                  <div style={{
                    marginTop: 12,
                    padding: '12px 14px',
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, lineHeight: 1.7 }}>
                      <strong style={{ color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: 4 }}>What we store:</strong>
                      • <code style={{ color: '#22d3ee', fontSize: 11 }}>cjm_cookie_consent</code> — your cookie preference (required)<br />
                      • No analytics, advertising, or tracking cookies<br />
                      • Forms go to our own secure system, with Google reCAPTCHA for spam protection<br />
                      • Google Fonts load from Google's CDN — see{' '}
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="cjm-cookie-link" style={{ color: '#22d3ee' }}>Google's policy</a>
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="cjm-cookie-actions">
              <button onClick={() => choose(true)} className="btn-primary cjm-cookie-btn">
                Accept All
              </button>
              <button onClick={() => choose(false)} className="btn-ghost cjm-cookie-btn">
                Decline
              </button>
            </div>

            <Link
              to="/privacy"
              onClick={() => setVisible(false)}
              className="cjm-cookie-link"
              style={{
                display: 'inline-flex', alignItems: 'center', minHeight: 32,
                marginTop: 6, fontSize: 11.5,
                color: 'rgba(255,255,255,0.5)', textDecoration: 'underline',
                textUnderlineOffset: 3, touchAction: 'manipulation',
              }}
            >
              Privacy Policy
            </Link>
          </div>

          <style>{`
            .cjm-cookie {
              position: fixed;
              bottom: 16px;
              left: 0;
              right: 0;
              margin-left: auto;
              margin-right: auto;
              z-index: 9995;
              width: min(400px, calc(100vw - 32px));
            }
            .cjm-cookie-inner {
              position: relative;
              overflow: hidden;
              background: rgba(10,10,18,0.72);
              backdrop-filter: blur(32px) saturate(140%);
              -webkit-backdrop-filter: blur(32px) saturate(140%);
              border: 1px solid rgba(6,182,212,0.16);
              border-radius: 16px;
              padding: 18px 20px;
              box-shadow: 0 24px 80px rgba(0,0,0,0.6), 0 0 50px rgba(6,182,212,0.09), 0 0 0 1px rgba(6,182,212,0.05), inset 0 1px 0 rgba(255,255,255,0.06);
            }
            .cjm-cookie-actions {
              display: flex;
              gap: 10px;
              margin-top: 14px;
            }
            .cjm-cookie-btn {
              flex: 1;
              justify-content: center;
              min-height: 44px;
              padding: 10px 18px;
              font-size: 13px;
              touch-action: manipulation;
              cursor: pointer;
            }
            .cjm-cookie-btn:focus-visible,
            .cjm-cookie-link:focus-visible {
              outline: 2px solid #22d3ee;
              outline-offset: 2px;
              border-radius: 8px;
            }
            @media (max-width: 480px) {
              .cjm-cookie {
                bottom: 12px;
                width: calc(100vw - 24px);
              }
              .cjm-cookie-inner { padding: 16px; }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
